/**
 * 글로벌 주문서 추가정보(통관 필드) — 다국어 몰별 항목 관리 FE 타입.
 * 근거: everline-qa-51-order-policy-analysis.md §4 (04.png·05.png, 고도몰 v5.1.23
 * "주문서 추가정보"). be-order 계약(order-service admin CRUD).
 *
 * 다국어 몰별(영문/중문/일문) 탭 구조에서 몰별 항목을 등록/수정/삭제한다. 각 항목은
 * 노출유형 5종(텍스트 한줄/여러줄·라디오·체크·셀렉트), 글자수 제한(텍스트형 1~250),
 * 옵션(선택형), 암호화·마스킹, 적용 상품 범위, 입력방법(공통 1회/상품별)을 갖는다.
 *
 * be-order 계약 확정 반영: mall 은 BCP-47 문자열(zh-CN/en-US/ja-JP, ≤16). 목록 응답
 * 키 = data.fields[]. 단건/생성/수정 = OrderFormFieldAdminResponse. 삭제=soft(data null).
 */

/**
 * 몰(다국어) 키 — be-order 계약 = BCP-47 문자열(storefront 로케일 쿠키와 동일).
 * §4 글로벌몰 탭은 영문/중문/일문 3종. mall 컬럼 자체는 ≤16 자유 문자열이라 향후
 * 다른 로케일도 수용하지만, BO 탭은 이 3종을 고정 노출한다.
 */
export const ORDER_FORM_MALLS = ['en-US', 'zh-CN', 'ja-JP'] as const;
export type OrderFormMall = (typeof ORDER_FORM_MALLS)[number];

/** 몰 탭 라벨(BO 한국어 전용). */
export const ORDER_FORM_MALL_LABELS: Record<OrderFormMall, string> = {
  'en-US': '영문몰',
  'zh-CN': '중문몰',
  'ja-JP': '일문몰',
};

/**
 * 노출유형 5종(§4 "텍스트박스(한줄/여러줄)·라디오버튼·체크박스·셀렉트박스").
 * TEXT_* 만 글자수 제한, RADIO/CHECKBOX/SELECT 만 옵션을 사용한다.
 */
export const FIELD_TYPES = [
  'TEXT_SINGLE',
  'TEXT_MULTI',
  'RADIO',
  'CHECKBOX',
  'SELECT',
] as const;
export type OrderFormFieldType = (typeof FIELD_TYPES)[number];

export const FIELD_TYPE_LABELS: Record<OrderFormFieldType, string> = {
  TEXT_SINGLE: '텍스트 (한줄)',
  TEXT_MULTI: '텍스트 (여러줄)',
  RADIO: '라디오 버튼',
  CHECKBOX: '체크박스',
  SELECT: '셀렉트 박스',
};

/** 텍스트형(글자수 제한·암호화/마스킹 대상) 여부. */
export const isTextFieldType = (t: OrderFormFieldType): boolean =>
  t === 'TEXT_SINGLE' || t === 'TEXT_MULTI';

/** 선택형(옵션 입력 대상) 여부. */
export const isOptionFieldType = (t: OrderFormFieldType): boolean =>
  t === 'RADIO' || t === 'CHECKBOX' || t === 'SELECT';

/** 적용 상품 범위(§4 "적용 상품 전체상품"). 예외조건(카테고리·브랜드·상품)은 후속. */
export const APPLY_SCOPES = ['ALL_PRODUCTS'] as const;
export type OrderFormApplyScope = (typeof APPLY_SCOPES)[number];

export const APPLY_SCOPE_LABELS: Record<OrderFormApplyScope, string> = {
  ALL_PRODUCTS: '전체상품',
};

/** 입력방법(§4 "공통으로 한 번만 입력 / 상품별"). */
export const INPUT_METHODS = ['COMMON_ONCE', 'PER_PRODUCT'] as const;
export type OrderFormInputMethod = (typeof INPUT_METHODS)[number];

export const INPUT_METHOD_LABELS: Record<OrderFormInputMethod, string> = {
  COMMON_ONCE: '공통으로 한 번만 입력',
  PER_PRODUCT: '상품별 입력',
};

/** 글자수 제한 경계(§4 "1~250"). */
export const MIN_MAX_LENGTH = 1;
export const MAX_MAX_LENGTH = 250;
export const DEFAULT_MAX_LENGTH = 50;

/**
 * OrderFormFieldAdminResponse — be-order 계약. 목록(data.fields[])·단건·생성·수정 응답
 * 모두 이 형태(전체 필드). = UpsertRequest 필드 + fieldId/createdAt/updatedAt.
 */
export interface OrderFormFieldDetail {
  fieldId: string;
  mall: OrderFormMall;
  label: string;
  description: string;
  displayEnabled: boolean;
  required: boolean;
  fieldType: OrderFormFieldType;
  maxLength: number;
  /** RADIO/CHECKBOX/SELECT 에서만 사용. 그 외 빈 배열. */
  options: string[];
  encrypt: boolean;
  mask: boolean;
  applyScope: OrderFormApplyScope;
  inputMethod: OrderFormInputMethod;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * 목록 행 = 단건 응답과 동일 형태(be-order 가 목록에 전체 필드를 내려준다).
 * 별도 요약 타입을 두지 않고 Detail 을 그대로 목록 행으로 쓴다.
 */
export type OrderFormFieldSummary = OrderFormFieldDetail;

/** POST/PUT body. fieldId/감사메타 제외. */
export interface OrderFormFieldPayload {
  mall: OrderFormMall;
  label: string;
  description: string;
  displayEnabled: boolean;
  required: boolean;
  fieldType: OrderFormFieldType;
  /** TEXT_* 에서만 의미(1~250). 그 외 BE 무시 — FE 는 DEFAULT 전송. */
  maxLength: number;
  /** 선택형에서만. 그 외 빈 배열 정규화. */
  options: string[];
  encrypt: boolean;
  mask: boolean;
  applyScope: OrderFormApplyScope;
  inputMethod: OrderFormInputMethod;
  sortOrder: number;
}

/** 신규 항목 기본값(§4 정본: 노출 ON·선택입력·텍스트 한줄·전체상품·공통 1회). */
export const DEFAULT_FIELD_PAYLOAD: OrderFormFieldPayload = {
  mall: 'zh-CN',
  label: '',
  description: '',
  displayEnabled: true,
  required: false,
  fieldType: 'TEXT_SINGLE',
  maxLength: DEFAULT_MAX_LENGTH,
  options: [],
  encrypt: false,
  mask: false,
  applyScope: 'ALL_PRODUCTS',
  inputMethod: 'COMMON_ONCE',
  sortOrder: 0,
};

/**
 * 목록 쿼리 파라미터. be-order: `?mall=zh-CN`(옵션, 미지정=전체 몰).
 * BO 는 몰별 탭이라 항상 활성 몰을 지정한다.
 */
export interface OrderFormFieldListParams {
  mall: OrderFormMall;
}

/**
 * BO 주문상세 추가정보 값(주문 시 구매자가 기입). be-order 계약:
 * GET `/api/admin/v1/orders/{orderId}/order-form-field-values`
 * → data: { orderId, values[]: OrderFormFieldValue }. 15차 D-18[ID144] 흐름.
 * masked=true 면 value 는 이미 마스킹된 표시값(앞1+***+뒤1)이라 FE 가공 불필요.
 */
export interface OrderFormFieldValue {
  fieldId: string;
  label: string;
  fieldType: OrderFormFieldType;
  /** 표시값. masked=true 면 마스킹된 값. 빈/미입력은 빈 문자열. */
  value: string;
  /** true 면 value 가 마스킹된 표시값(원본 비노출). */
  masked: boolean;
}

/** GET .../order-form-field-values 응답 data. */
export interface OrderFormFieldValues {
  orderId: string;
  values: OrderFormFieldValue[];
}
